import { SupportedLanguages } from "../../../../libs/i18next/language";
import { Utilities } from "../../../../utils/utilities";
import { StringUtilities } from "../../../../utils/stringUtilities";
import { CollectionConnections } from "../../Company/Collections/CompanyCollectionData";
import CollectionLoader, { CollectionLoadedListener } from "../../General/CollectionLoader";
import { ReadAccess } from "../../General/AccessData";
import { FirebaseData } from "../../General/FirebaseData";
import { LocalData } from "../../General/LocalData";
import { TranslationData } from "../../General/TranslationData";
import { PropertyData } from "../PropertyData";
import { PropertyCollectionItemData } from "./PropertyCollectionItemData";
import { PropertyCustomFieldData } from "./PropertyCustomFieldData";
import { query, where } from "firebase/firestore";

export class PropertyCollectionData extends FirebaseData {
  customFields: CollectionLoader<PropertyCustomFieldData>;
  items: CollectionLoader<PropertyCollectionItemData>;

  standardLangCode: SupportedLanguages = SupportedLanguages.EN;
  name: TranslationData = new TranslationData();
  icon: string = "";
  previewFields: string[] = [];
  itemCount: number = 0;
  connections: CollectionConnections = { deviations: true, routines: true, propertyFiles: true };
  readAccess: ReadAccess = {};
  requiredWriteAccess: ReadAccess | undefined = undefined;
  archived: boolean = false;
  customFieldsOrder: string[] = [];

  constructor(id: string, collectionName: string, parent: FirebaseData | undefined) {
    super(id, collectionName, parent);
    this.customFields = new CollectionLoader("customFields", this, PropertyCustomFieldData);
    this.items = new CollectionLoader("items", this, PropertyCollectionItemData);
  }

  setup(data: any) {
    this.standardLangCode = SupportedLanguages.getLanguageWithCode(data.standardLangCode);
    this.name.setup(data.name, this.standardLangCode);
    this.archived = typeof data.archived === "boolean" ? data.archived : false;
    if (this.isValidString(data.icon)) {
      this.icon = data.icon;
    } else if (this.name.translate().length > 0 && this.name.translate().split(" ").length > 0) {
      let firstChar = this.name.translate().split(" ")[0];
      if (StringUtilities.stringIsEmoji(firstChar)) this.icon = firstChar;
    }
    this.previewFields = Array.isArray(data.previewFields) ? data.previewFields.filter((field: any) => typeof field === "string") : [];
    this.itemCount = typeof data.itemCount === "number" ? data.itemCount : 0;
    this.customFieldsOrder = Array.isArray(data.customFieldsOrder) ? data.customFieldsOrder : [];

    this.readAccess = Utilities.getReadAccess(data.readAccess);
    this.requiredWriteAccess = Utilities.getOptionalReadAccess(data.requiredWriteAccess);

    this.connections = { deviations: true, routines: true, propertyFiles: true };
    if (typeof data.connections === "object") {
      this.connections = {
        deviations: typeof data.connections.deviations === "boolean" ? data.connections.deviations : true,
        routines: typeof data.connections.routines === "boolean" ? data.connections.routines : true,
        propertyFiles: typeof data.connections.propertyFiles === "boolean" ? data.connections.propertyFiles : true,
      };
    }

    super.setup(data);
  }

  loadAllItems(): Promise<void> {
    return new Promise((resolve) => {
      const property = this.parent instanceof PropertyData ? this.parent : undefined;
      const member = property?.members.getData(LocalData.currentUser.id);
      const memberRoles = member?.roles.map((role) => role.id);
      let loadedCount = 0;
      let toLoad = 1;

      if (memberRoles?.length) {
        this.items.loadWithFilter(
          (ref) => query(ref, where("readAccess.roles", "array-contains-any", memberRoles)),
          () => {
            loadedCount++;
            if (loadedCount >= toLoad) resolve();
          }
        );
      }

      if (member?.contractorGroups.length) {
        toLoad++;
        this.items.loadWithFilter(
          (ref) =>
            query(
              ref,
              where(
                "readAccess.contractorGroups",
                "array-contains-any",
                member.contractorGroups.map((group) => group.id)
              )
            ),
          () => {
            loadedCount++;
            if (loadedCount >= toLoad) resolve();
          }
        );
      }
    });
  }

  setupItemsListener(onUpdate: () => void): CollectionLoadedListener<PropertyCollectionItemData>[] {
    const property = this.parent instanceof PropertyData ? this.parent : undefined;
    const member = property?.members.getData(LocalData.currentUser.id);
    const listeners: CollectionLoadedListener<PropertyCollectionItemData>[] = [];

    this.items.documents = [];

    if (member && member.roles.length > 0) {
      listeners.push(
        this.items.setupRealtimeListenerWithFilter(
          (ref) =>
            query(
              ref,
              where(
                "readAccess.roles",
                "array-contains-any",
                member.roles.map((role) => role.id)
              )
            ),
          onUpdate
        )
      );
    }

    if (member && member.contractorGroups.length > 0) {
      listeners.push(
        this.items.setupRealtimeListenerWithFilter(
          (ref) =>
            query(
              ref,
              where(
                "readAccess.contractorGroups",
                "array-contains-any",
                member.contractorGroups.map((group) => group.id)
              )
            ),
          onUpdate
        )
      );
    }

    if (member && member.units.length > 0) {
      listeners.push(
        this.items.setupRealtimeListenerWithFilter(
          (ref) =>
            query(
              ref,
              where(
                "readAccess.propertyUnits",
                "array-contains-any",
                member.units.map((unit) => unit.parent?.id + "/" + unit.id)
              )
            ),
          () => onUpdate()
        )
      );
    }

    return listeners;
  }
  setupCustomFieldsListener(onUpdate: () => void): CollectionLoadedListener<PropertyCustomFieldData>[] {
    const property = this.parent instanceof PropertyData ? this.parent : undefined;
    const member = property?.members.getData(LocalData.currentUser.id);
    const listeners: CollectionLoadedListener<PropertyCustomFieldData>[] = [];

    if (member && member.roles.length > 0) {
      listeners.push(
        this.customFields.setupRealtimeListenerWithFilter(
          (ref) =>
            query(
              ref,
              where(
                "readAccess.roles",
                "array-contains-any",
                member.roles.map((role) => role.id)
              )
            ),
          () => {
            onUpdate();
          }
        )
      );
    }

    if (member && member.contractorGroups.length > 0) {
      listeners.push(
        this.customFields.setupRealtimeListenerWithFilter(
          (ref) =>
            query(
              ref,
              where(
                "readAccess.contractorGroups",
                "array-contains-any",
                member?.contractorGroups.map((group) => group.id)
              )
            ),
          onUpdate
        )
      );
    }

    return listeners;
  }

  loadCustomFields(onUpdate: (fields: PropertyCustomFieldData[]) => void) {
    const property = this.parent instanceof PropertyData ? this.parent : undefined;
    const member = property?.members.getData(LocalData.currentUser.id);
    let loadedCount = 0;
    let totalToLoad = 0;

    if (member && member.roles.length > 0) {
      totalToLoad++;
      this.customFields.loadWithFilter(
        (ref) =>
          query(
            ref,
            where(
              "readAccess.roles",
              "array-contains-any",
              member.roles.map((role) => role.id)
            )
          ),
        () => {
          loadedCount++;
          if (loadedCount >= totalToLoad) onUpdate(this.customFields.documents);
        }
      );
    }

    if (member && member.contractorGroups.length > 0) {
      totalToLoad++;
      this.customFields.loadWithFilter(
        (ref) =>
          query(
            ref,
            where(
              "readAccess.contractorGroups",
              "array-contains-any",
              member?.contractorGroups.map((group) => group.id)
            )
          ),
        () => {
          loadedCount++;
          if (loadedCount >= totalToLoad) onUpdate(this.customFields.documents);
        }
      );
    }
  }
}

